import React from "react";
import { DECK } from "@/data/pitchContent";
import { ASSETS } from "@/data/content";

export const SLIDE_W = 1280;
export const SLIDE_H = 720;

/* Real WorkPay logo (dark text + green icon) for light surfaces */
export const DeckLogo = ({ height = 26, className = "" }) => (
  <img src={ASSETS.logo} alt="WorkPay" className={className} style={{ height, width: "auto", display: "block" }} />
);

/* Kicker / section label */
export const Kicker = ({ children, onDark = false }) => (
  <span
    className="inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.28em]"
    style={{ color: onDark ? DECK.accent : DECK.charcoal }}
  >
    <span className="h-[3px] w-7 rounded-full" style={{ background: DECK.accent }} />
    {children}
  </span>
);

/* WorkPay wordmark for cover / footers */
export const Wordmark = ({ className = "", onDark = false }) => (
  <span className={`font-display font-extrabold tracking-tight ${className}`} style={{ color: onDark ? "#fff" : DECK.ink }}>
    Work<span style={{ color: onDark ? DECK.accent : DECK.ink }}>pay</span>
  </span>
);

/* Big headline with optional highlighted word via <mark> handling */
export const Headline = ({ children, className = "", onDark = false }) => (
  <h2
    className={`font-display font-extrabold tracking-tight leading-[1.04] ${className}`}
    style={{ color: onDark ? "#fff" : DECK.ink }}
  >
    {children}
  </h2>
);

/* Highlighter — green marker behind text */
export const Mark = ({ children }) => (
  <span className="relative inline-block">
    <span
      className="absolute left-0 right-0 bottom-[0.08em] h-[0.42em] -z-0 rounded-sm"
      style={{ background: DECK.accent }}
    />
    <span className="relative z-10">{children}</span>
  </span>
);

/* Flow chip + connector used in cover / solution diagrams */
export const FlowRow = ({ steps, highlightIndex = -1, onDark = false }) => (
  <div className="flex items-center gap-3 flex-wrap">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div
          className="rounded-full px-5 py-2.5 text-[15px] font-bold whitespace-nowrap"
          style={
            i === highlightIndex
              ? { background: DECK.accent, color: DECK.ink }
              : onDark
              ? { background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }
              : { background: "#fff", color: DECK.ink, border: "1px solid rgba(11,11,15,0.10)", boxShadow: "0 8px 24px -14px rgba(11,11,15,0.25)" }
          }
        >
          {s}
        </div>
        {i < steps.length - 1 && (
          <svg width="26" height="14" viewBox="0 0 26 14" fill="none" className="shrink-0">
            <path d="M1 7h22m0 0-5-5m5 5-5 5" stroke={DECK.accentDeep} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </React.Fragment>
    ))}
  </div>
);

/* Monogram avatar */
export const Avatar = ({ initials, size = 84 }) => (
  <div
    className="rounded-2xl flex items-center justify-center font-display font-extrabold"
    style={{ width: size, height: size, background: DECK.ink, color: DECK.accent, fontSize: size * 0.4 }}
  >
    {initials}
  </div>
);

/* Pale rounded card */
export const SoftCard = ({ children, className = "", style = {} }) => (
  <div
    className={`rounded-3xl ${className}`}
    style={{ background: "#fff", border: "1px solid rgba(11,11,15,0.08)", boxShadow: "0 18px 50px -28px rgba(11,11,15,0.30)", ...style }}
  >
    {children}
  </div>
);

/* Numbered pill */
export const NumPill = ({ n, onDark = false }) => (
  <span
    className="h-9 w-9 rounded-full flex items-center justify-center font-display text-[15px] font-extrabold shrink-0"
    style={{ background: DECK.accent, color: DECK.ink }}
  >
    {n}
  </span>
);

/* Slide footer brand strip (bottom-right) */
export const SlideMeta = ({ index, total, onDark = false }) => (
  <div
    className="absolute bottom-7 left-20 right-20 flex items-center justify-between text-[12px] font-semibold tracking-wide"
    style={{ color: onDark ? "rgba(255,255,255,0.55)" : DECK.muted }}
  >
    <DeckLogo height={30} />
    <span className="uppercase tracking-[0.22em]">
      {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
    </span>
  </div>
);
